import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";

const PRINCIPLES = [
  { t: "Indépendance", d: "Une analyse objective et un conseil libre de toute influence, au service exclusif de la qualité des décisions, de la gouvernance et de la réussite durable des projets." },
  { t: "Exigence", d: "Une approche fondée sur la rigueur méthodologique, l'expérience opérationnelle et une analyse approfondie, afin de produire des recommandations fiables, applicables et créatrices de valeur." },
  { t: "Vision stratégique", d: "Une lecture globale des projets et de leur écosystème pour anticiper les enjeux, aligner les parties prenantes et accompagner des décisions durables." },
  { t: "Impact durable", d: "Une ambition constante : renforcer la gouvernance, améliorer la performance et contribuer à des projets qui créent une valeur économique, sociale et environnementale durable." },
];

export default function About() {
  useSEO({
    fullTitle: "HERUS | Gouvernance des projets stratégiques en Afrique",
    description:
      "Découvrez HERUS, maison d'expertise en gouvernance, structuration, performance et impact des projets stratégiques en Afrique.",
    keywords: "HERUS Axiom, gouvernance des projets stratégiques, structuration de projets, performance, impact, Afrique, maison d'expertise, PPP, transition énergétique",
    path: "/a-propos",
  });
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <div data-testid="about-page" className="bg-[#F9F6F0]">
      {/* Hero — Section 2 */}
      <section className="pt-40 lg:pt-52 pb-20 lg:pb-28 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal><Overline>À propos</Overline></Reveal>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.95] tracking-tight text-[#0A0A0A] text-4xl sm:text-5xl lg:text-7xl max-w-5xl"
        >
          Maison d'expertise en gouvernance
          <br />
          <span className="italic text-[#C5A059]">des projets stratégiques</span>
        </motion.h1>

        <div className="mt-12 max-w-2xl space-y-8">
          <Reveal i={1}>
            <p className="text-[#0A0A0A]/75 text-lg lg:text-xl font-light leading-relaxed">
              HERUS est une maison d'expertise spécialisée dans la gouvernance, la structuration, la
              performance et l'impact des projets stratégiques. Nous accompagnons les acteurs publics
              et privés qui portent des projets à forte complexité, en Afrique.
            </p>
          </Reveal>
          <Reveal i={2}>
            <p className="text-[#0A0A0A]/60 text-base lg:text-lg font-light leading-relaxed">
              Notre singularité tient à une vision globale des projets : nous relions la cohérence
              stratégique, la solidité de la gouvernance et la maîtrise des risques, là où d'autres
              les traitent séparément. Cette lecture d'ensemble, conjuguée à une double expertise
              stratégique et opérationnelle, éclaire les décisions et sécurise durablement les projets.
            </p>
          </Reveal>
          <Reveal i={3}>
            <Link to="/approche-gouvernance-projets" data-testid="about-hero-cta" className="group inline-flex items-center gap-3 mt-2 text-sm uppercase tracking-[0.2em] text-[#0A0A0A] border-b border-[#C5A059] pb-2">
              Découvrir notre approche
              <ArrowUpRight size={18} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Vision & Mission — Sections 3 & 4 */}
      <section className="border-t border-[#0A0A0A]/10 py-20 lg:py-32">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Overline>Notre vision</Overline>
            <h2 className="mt-6 font-serif-display text-3xl lg:text-4xl font-light text-[#0A0A0A] leading-tight">
              Contribuer à élever les standards de gouvernance des projets et des organisations.
            </h2>
            <p className="mt-6 text-[#0A0A0A]/65 font-light leading-relaxed">
              Nous portons l'ambition d'une expertise indépendante, exigeante et reconnue, au service
              de décisions plus éclairées et de projets créateurs de valeur. À travers nos
              interventions, nous contribuons à la performance des organisations, à la résilience des
              territoires et à la réussite des transformations.
            </p>
          </Reveal>
          <Reveal i={1}>
            <Overline>Notre mission</Overline>
            <h2 className="mt-6 font-serif-display text-3xl lg:text-4xl font-light text-[#0A0A0A] leading-tight">
              Concevoir aujourd'hui les fondations des projets de demain.
            </h2>
            <p className="mt-6 text-[#0A0A0A]/65 font-light leading-relaxed">
              Nous accompagnons les organisations dans les décisions qui conditionnent la réussite de
              leurs projets. Notre mission : renforcer leur structuration, leur gouvernance et leur
              capacité d'exécution afin de transformer des ambitions en résultats durables.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-[#0A0A0A]">
        <div className="grid lg:grid-cols-12">
          <div ref={ref} className="lg:col-span-5 relative overflow-hidden grain min-h-[520px] lg:min-h-[760px]">
            <motion.img
              style={{ y }}
              src={IMAGES.founderReal}
              alt="Léonie Stéphanie HONBAN, fondatrice de HERUS Axiom"
              className="absolute inset-0 h-[112%] w-full object-cover object-top"
            />
          </div>
          <div className="lg:col-span-7 px-6 lg:px-20 py-20 lg:py-40 flex items-center">
            <div>
              <Reveal><Overline>La fondatrice</Overline></Reveal>
              <Reveal i={1}>
                <h2 className="mt-6 font-serif-display text-4xl lg:text-6xl font-light text-[#F9F6F0] leading-none">
                  Léonie Stéphanie
                  <br /> <span className="italic text-[#C5A059]">HONBAN.</span>
                </h2>
              </Reveal>
              <Reveal i={2}>
                <p className="mt-8 max-w-xl text-[#F9F6F0]/70 font-light leading-relaxed">
                  Ingénieure de formation et spécialiste des projets industriels et énergétiques
                  internationaux, elle a bâti son parcours à la croisée de l'ingénierie, du management
                  stratégique et des partenariats public-privé.
                </p>
              </Reveal>
              <Reveal i={3}>
                <p className="mt-6 max-w-xl text-[#F9F6F0]/70 font-light leading-relaxed">
                  La réussite durable des projets les plus ambitieux repose avant tout sur une
                  préparation rigoureuse, une gouvernance adaptée et une vision partagée. Elle est
                  aujourd'hui au cœur de l'identité de HERUS.
                </p>
              </Reveal>
              <Reveal i={4}>
                <Link to="/approche-gouvernance-projets" data-testid="about-approach-cta" className="group inline-flex items-center gap-3 mt-10 text-sm uppercase tracking-[0.2em] text-[#F9F6F0] border border-white/25 px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-500">
                  Notre approche
                  <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Principes d'action — Section 5 */}
      <section className="bg-[#F9F6F0] py-24 lg:py-32">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <Reveal>
            <Overline>Nos principes d'action</Overline>
            <h2 className="mt-6 font-serif-display text-3xl lg:text-4xl font-light italic text-[#0A0A0A]">
              Des convictions fortes qui guident chacune de nos interventions.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 border-t border-l border-[#0A0A0A]/10">
            {PRINCIPLES.map((v, i) => (
              <Reveal key={i} i={i} className="border-b border-r border-[#0A0A0A]/10 p-10 lg:p-14">
                <span className="font-serif-display text-5xl text-[#C5A059]">0{i + 1}</span>
                <h3 className="mt-8 font-serif-display text-2xl lg:text-3xl text-[#0A0A0A]">{v.t}</h3>
                <p className="mt-3 text-[#0A0A0A]/60 font-light leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
